import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Collapse,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  TableSortLabel,
} from '@mui/material';
import {
  History as HistoryIcon,
  SwapHoriz as TransferIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SelectAll as SelectAllIcon,
  DeselectOutlined as DeselectIcon,
  Search as SearchIcon,
  SortByAlpha as SortIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';
import { useAuth } from '../context/AuthContext';
import { getCurrentSchoolYear } from '../lib/tables';

export default function ClassManagement() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [classHistory, setClassHistory] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  const [changeData, setChangeData] = useState({
    clasa_noua: '',
    specializare_noua: '',
    motiv: 'promovare',
    transfer_credite: true,
  });

  
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  const [promotionProgress, setPromotionProgress] = useState({ current: 0, total: 0 });
  const [promotionInProgress, setPromotionInProgress] = useState(false);
  const [expandedClasses, setExpandedClasses] = useState(new Set());

  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [filterSpec, setFilterSpec] = useState('all');
  const [sortField, setSortField] = useState('nume');
  const [sortDirection, setSortDirection] = useState('asc');

  
  const getClassesForSpecialization = (spec) => {
    if (!spec) return [];
    return [9, 10, 11, 12].map(an => `${an}${spec}`);
  };
  
  const specializations = [
    { code: 'MI', name: 'Matematică-Informatică' },
    { code: 'SN', name: 'Științe ale Naturii' },
    { code: 'SS', name: 'Științe Sociale' },
    { code: 'FL', name: 'Filologie' },
    { code: 'BU', name: 'Business' },
  ];
  
  const getSpecializationName = (code) => {
    const found = specializations.find(s => s.code === code);
    return found ? found.name : code || 'Nespecificată';
  };
  const motiveSchimbare = [
    { value: 'promovare', label: 'Promovare în anul următor' },
    { value: 'transfer', label: 'Transfer la altă clasă/specializare' },
    { value: 'repetent', label: 'Repetent' },
    { value: 'corectie', label: 'Corecție date' },
  ];

  
  const availableClasses = useMemo(() => {
    const classSet = new Set(students.map(s => s.clasa).filter(Boolean));
    return Array.from(classSet).sort();
  }, [students]);

  
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = [...students];
    
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.nume?.toLowerCase().includes(term) ||
        s.prenume?.toLowerCase().includes(term) ||
        s.email?.toLowerCase().includes(term)
      );
    }
    
    
    if (filterClass !== 'all') {
      filtered = filtered.filter(s => s.clasa === filterClass);
    }
    
    
    if (filterSpec !== 'all') {
      filtered = filtered.filter(s => s.specializare === filterSpec);
    }
    
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'nume':
          aVal = `${a.nume} ${a.prenume}`.toLowerCase();
          bVal = `${b.nume} ${b.prenume}`.toLowerCase();
          break;
        case 'clasa':
          aVal = a.clasa || '';
          bVal = b.clasa || '';
          break;
        case 'credite':
          aVal = a.totalCredits || 0;
          bVal = b.totalCredits || 0;
          break;
        case 'specializare':
          aVal = a.specializare || '';
          bVal = b.specializare || '';
          break;
        default:
          aVal = a.nume?.toLowerCase() || '';
          bVal = b.nume?.toLowerCase() || '';
      }
      
      if (sortField === 'credite') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    });
    
    return filtered;
  }, [students, searchTerm, filterClass, filterSpec, sortField, sortDirection]);

  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  
  const clearFilters = () => {
    setSearchTerm('');
    setFilterClass('all');
    setFilterSpec('all');
    setSortField('nume');
    setSortDirection('asc');
  };

  
  const hasActiveFilters = searchTerm || filterClass !== 'all' || filterSpec !== 'all';

  useEffect(() => {
    let isMounted = true;
    
    const loadAsync = async () => {
      if (profile && profile.rol === 'coordonator' && isMounted) {
        await loadStudents(isMounted);
      }
    };
    
    loadAsync();
    return () => { isMounted = false; };
  }, [profile]);

  const loadStudents = async (isMounted = true) => {
    setLoading(true);
    try {
      const { data: studentsData } = await supabase
        .from(TABLES.profiles)
        .select('*')
        .eq('rol', 'elev')
        .order('clasa')
        .order('nume');

      if (!isMounted) return;

      
      const studentsWithCredits = await Promise.all(
        (studentsData || []).map(async (student) => {
          
          const { data: requestsData } = await supabase
            .from(TABLES.credit_requests)
            .select('credite_acordate')
            .eq('id_elev', student.id)
            .eq('status', 'validat');

          const totalCredits = requestsData?.reduce((sum, r) => sum + (r.credite_acordate || 0), 0) || 0;

          return {
            ...student,
            totalCredits,
          };
        })
      );

      if (isMounted) {
        setStudents(studentsWithCredits);
      }
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const loadStudentHistory = async (studentId) => {
    try {
      const { data } = await supabase
        .from(TABLES.student_class_history)
        .select('*')
        .eq('id_elev', studentId)
        .order('data_schimbare', { ascending: false });

      setClassHistory(data || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleOpenChangeDialog = (student) => {
    setSelectedStudent(student);
    setChangeData({
      clasa_noua: student.clasa || '',
      specializare_noua: student.specializare || '',
      motiv: 'promovare',
      transfer_credite: true,
    });
    setDialogOpen(true);
  };

  const handleOpenHistoryDialog = async (student) => {
    setSelectedStudent(student);
    await loadStudentHistory(student.id);
    setHistoryDialogOpen(true);
  };

  const handleSaveClassChange = async () => {
    if (!selectedStudent || !changeData.clasa_noua) return;

    setSaving(true);
    try {
      const anScolar = getCurrentSchoolYear();
      const anScolarVechi = selectedStudent.an_scolar_curent || anScolar;
      
      
      const { error: historyError } = await supabase
        .from(TABLES.student_class_history)
        .insert({
          id_elev: selectedStudent.id,
          clasa_veche: selectedStudent.clasa,
          clasa_noua: changeData.clasa_noua,
          specializare_veche: selectedStudent.specializare,
          specializare_noua: changeData.specializare_noua,
          an_scolar: anScolarVechi,
          motiv: changeData.motiv,
          credite_totale_la_schimbare: selectedStudent.totalCredits,
          credite_transferate: changeData.transfer_credite ? selectedStudent.totalCredits : 0,
          creat_de: profile.id,
        });

      if (historyError) throw historyError;

      
      const { error: updateError } = await supabase
        .from(TABLES.profiles)
        .update({
          clasa: changeData.clasa_noua,
          clasa_anterioara: selectedStudent.clasa,
          specializare: changeData.specializare_noua,
          an_scolar_curent: anScolar,
        })
        .eq('id', selectedStudent.id);

      if (updateError) throw updateError;

      setDialogOpen(false);
      loadStudents();
      alert(`Elevul ${selectedStudent.nume} ${selectedStudent.prenume} a fost mutat în clasa ${changeData.clasa_noua}. Creditele au fost reportate în anul școlar nou.`);
      
    } catch (error) {
      console.error('Error saving class change:', error);
      alert('Eroare la salvarea schimbării: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getMotivLabel = (motiv) => {
    const found = motiveSchimbare.find(m => m.value === motiv);
    return found ? found.label : motiv;
  };

  const getMotivColor = (motiv) => {
    switch (motiv) {
      case 'promovare': return 'success';
      case 'transfer': return 'info';
      case 'repetent': return 'warning';
      case 'corectie': return 'default';
      default: return 'default';
    }
  };

  
  const getNextClass = (currentClass) => {
    if (!currentClass) return null;
    const match = currentClass.match(/^(\d+)(.+)$/);
    if (!match) return null;
    const year = parseInt(match[1]);
    const specialization = match[2];
    if (year >= 12) return 'ABSOLVENT'; 
    return `${year + 1}${specialization}`;
  };

  
  const promotableStudents = useMemo(() => {
    return students.filter(s => {
      if (!s.clasa) return false;
      if (s.absolvent) return false;
      if (s.an_scolar_curent === getCurrentSchoolYear()) return false;
      const match = s.clasa.match(/^(\d+)/);
      if (!match) return false;
      const year = parseInt(match[1]);
      return year >= 9 && year <= 12;
    });
  }, [students]);

  
  const studentsForPromotionByClass = useMemo(() => {
    const grouped = {};
    promotableStudents.forEach(student => {
      const clasa = student.clasa;
      if (!grouped[clasa]) grouped[clasa] = [];
      grouped[clasa].push({
        ...student,
        nextClass: getNextClass(clasa)
      });
    });
    return grouped;
  }, [promotableStudents]);

  
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  
  const toggleClassSelection = (clasa, students) => {
    const studentIds = students.map(s => s.id);
    const allSelected = studentIds.every(id => selectedStudents.has(id));
    
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        studentIds.forEach(id => newSet.delete(id));
      } else {
        studentIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  
  const selectAll = () => {
    const allIds = promotableStudents.map(s => s.id);
    setSelectedStudents(new Set(allIds));
  };

  
  const deselectAll = () => {
    setSelectedStudents(new Set());
  };

  
  const toggleClassExpand = (clasa) => {
    setExpandedClasses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clasa)) {
        newSet.delete(clasa);
      } else {
        newSet.add(clasa);
      }
      return newSet;
    });
  };

  
  const handleMassPromotion = async () => {
    if (selectedStudents.size === 0) return;

    setPromotionInProgress(true);
    setPromotionProgress({ current: 0, total: selectedStudents.size });
    
    const anScolar = getCurrentSchoolYear();
    let successCount = 0;
    let errors = [];
    let currentIndex = 0;

    for (const studentId of selectedStudents) {
      currentIndex++;
      setPromotionProgress({ current: currentIndex, total: selectedStudents.size });

      const student = students.find(s => s.id === studentId);
      if (!student) continue;

      const nextClass = getNextClass(student.clasa);
      if (!nextClass) continue;

      try {
        const anScolarVechi = student.an_scolar_curent || anScolar;

        
        const { error: historyError } = await supabase
          .from(TABLES.student_class_history)
          .insert({
            id_elev: student.id,
            clasa_veche: student.clasa,
            clasa_noua: nextClass === 'ABSOLVENT' ? 'Absolvent' : nextClass,
            specializare_veche: student.specializare,
            specializare_noua: student.specializare,
            an_scolar: anScolarVechi,
            motiv: 'promovare',
            credite_totale_la_schimbare: student.totalCredits,
            credite_transferate: student.totalCredits,
            creat_de: profile.id,
          });

        if (historyError) throw historyError;

        
        if (nextClass === 'ABSOLVENT') {
          const { error: absolventError } = await supabase
            .from(TABLES.profiles)
            .update({
              clasa_anterioara: student.clasa,
              an_scolar_curent: anScolar,
              absolvent: true,
              data_absolvirii: new Date().toISOString(),
            })
            .eq('id', student.id);

          if (absolventError) throw absolventError;
        } else {
          const { error: updateError } = await supabase
            .from(TABLES.profiles)
            .update({
              clasa: nextClass,
              clasa_anterioara: student.clasa,
              an_scolar_curent: anScolar,
            })
            .eq('id', student.id);

          if (updateError) throw updateError;
        }

        successCount++;
      } catch (error) {
        console.error(`Error promoting student ${student.id}:`, error);
        errors.push(`${student.nume} ${student.prenume}: ${error.message}`);
      }
    }

    setPromotionInProgress(false);
    setPromotionDialogOpen(false);
    setSelectedStudents(new Set());
    loadStudents();

    if (errors.length > 0) {
      alert(`Promovare finalizată!\n\nSucces: ${successCount} elevi\nErori: ${errors.length}\n\nDetalii erori:\n${errors.join('\n')}`);
    } else {
      alert(`Promovare finalizată cu succes!\n\n${successCount} elevi au fost promovați în anul următor.`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (profile?.rol !== 'coordonator') {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert severity="error">
          Doar coordonatorii pot accesa această pagină.
        </Alert>
      </Container>
    );
  }

  
  const studentsByClass = filteredAndSortedStudents.reduce((acc, student) => {
    const clasa = student.clasa || 'Fără clasă';
    if (!acc[clasa]) acc[clasa] = [];
    acc[clasa].push(student);
    return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header - Compact */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
            <SchoolIcon />
          </Avatar>
          Gestiune Clase și Istoric
        </Typography>
      </Box>

      {/* Filter Bar - afișat pentru Tab 0 și Tab 1 */}
      {(tabValue === 0 || tabValue === 1) && (
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Search */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Caută elev..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* Filter by Class */}
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Clasă</InputLabel>
                  <Select
                    value={filterClass}
                    label="Clasă"
                    onChange={(e) => setFilterClass(e.target.value)}
                  >
                    <MenuItem value="all">Toate clasele</MenuItem>
                    {availableClasses.map((cls) => (
                      <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Filter by Specialization */}
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Specializare</InputLabel>
                  <Select
                    value={filterSpec}
                    label="Specializare"
                    onChange={(e) => setFilterSpec(e.target.value)}
                  >
                    <MenuItem value="all">Toate</MenuItem>
                    {specializations.map((spec) => (
                      <MenuItem key={spec.code} value={spec.code}>{spec.code}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Sort */}
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sortare</InputLabel>
                  <Select
                    value={sortField}
                    label="Sortare"
                    onChange={(e) => setSortField(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <SortIcon fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="nume">Nume</MenuItem>
                    <MenuItem value="clasa">Clasă</MenuItem>
                    <MenuItem value="credite">Credite</MenuItem>
                    <MenuItem value="specializare">Specializare</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Sort Direction & Clear */}
              <Grid item xs={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                    sx={{ minWidth: 90 }}
                  >
                    {sortDirection === 'asc' ? '↑ Cresc.' : '↓ Desc.'}
                  </Button>
                  {hasActiveFilters && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                    >
                      Resetează
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
            
            {/* Active filters chips */}
            {hasActiveFilters && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Filtre active:
                </Typography>
                {searchTerm && (
                  <Chip 
                    label={`Căutare: "${searchTerm}"`} 
                    size="small" 
                    onDelete={() => setSearchTerm('')}
                  />
                )}
                {filterClass !== 'all' && (
                  <Chip 
                    label={`Clasă: ${filterClass}`} 
                    size="small" 
                    color="primary"
                    onDelete={() => setFilterClass('all')}
                  />
                )}
                {filterSpec !== 'all' && (
                  <Chip 
                    label={`Spec: ${filterSpec}`} 
                    size="small" 
                    color="secondary"
                    onDelete={() => setFilterSpec('all')}
                  />
                )}
                <Chip 
                  label={`${filteredAndSortedStudents.length} din ${students.length} elevi`} 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs 
        value={tabValue} 
        onChange={(e, v) => setTabValue(v)} 
        sx={{ 
          mb: 3,
          '& .MuiTab-root': { minHeight: 48 }
        }}
      >
        <Tab label={`Pe Clase (${Object.keys(studentsByClass).length})`} />
        <Tab label={`Lista Completă (${filteredAndSortedStudents.length})`} />
        <Tab label="Promovare în Masă" icon={<TrendingUpIcon />} iconPosition="start" />
      </Tabs>

      {/* Tab 1: Pe clase - Cu carduri uniforme */}
      {tabValue === 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
          {Object.entries(studentsByClass).sort().map(([clasa, studentsInClass], index) => (
            <Box key={clasa} sx={{ flex: index === Object.keys(studentsByClass).length - 1 ? { xs: '0 0 100%', sm: '0 0 calc(50% - 12px)', lg: '0 0 calc(33.333% - 16px)' } : { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(50% - 12px)', lg: '0 0 calc(33.333% - 16px)' }, display: 'flex' }}>
              <Card sx={{ 
                borderRadius: 3, 
                height: '100%',
                width: '100%',
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 4 }
              }}>
                {/* Header clasă */}
                <Box sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  p: 2, 
                  borderRadius: '12px 12px 0 0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="h6" fontWeight="bold">
                    Clasa {clasa}
                  </Typography>
                  <Chip 
                    label={`${studentsInClass.length} elevi`} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
                
                <CardContent sx={{ 
                  flex: 1, 
                  p: 1.5, 
                  maxHeight: 300, 
                  overflow: 'auto',
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 3 }
                }}>
                  {studentsInClass.map((student) => (
                    <Box 
                      key={student.id}
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1,
                        mb: 0.5,
                        borderRadius: 2,
                        bgcolor: '#f8f9fa',
                        '&:hover': { bgcolor: '#e3f2fd' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.8rem' }}>
                          {student.nume?.[0]}{student.prenume?.[0]}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            {student.nume} {student.prenume}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip 
                              label={student.specializare || 'N/A'} 
                              size="small" 
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            <Chip 
                              label={`${student.totalCredits} cr`} 
                              size="small" 
                              color={student.totalCredits > 0 ? 'success' : 'default'}
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Vezi istoric">
                          <IconButton size="small" onClick={() => handleOpenHistoryDialog(student)}>
                            <HistoryIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Schimbă clasa">
                          <IconButton size="small" color="primary" onClick={() => handleOpenChangeDialog(student)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          ))}
          
          {Object.keys(studentsByClass).length === 0 && (
            <Box sx={{ width: '100%' }}>
              <Alert severity="info">
                Nu există elevi care să corespundă filtrelor selectate.
              </Alert>
            </Box>
          )}
        </Box>
      )}

      {/* Tab 2: Lista completă - Tabel îmbunătățit */}
      {tabValue === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'nume'}
                    direction={sortField === 'nume' ? sortDirection : 'asc'}
                    onClick={() => handleSort('nume')}
                    sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                  >
                    Elev
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'clasa'}
                    direction={sortField === 'clasa' ? sortDirection : 'asc'}
                    onClick={() => handleSort('clasa')}
                    sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                  >
                    Clasă
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'specializare'}
                    direction={sortField === 'specializare' ? sortDirection : 'asc'}
                    onClick={() => handleSort('specializare')}
                    sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                  >
                    Specializare
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortField === 'credite'}
                    direction={sortField === 'credite' ? sortDirection : 'asc'}
                    onClick={() => handleSort('credite')}
                    sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                  >
                    Credite
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>An Școlar</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedStudents.map((student, index) => (
                <TableRow 
                  key={student.id} 
                  hover
                  sx={{ bgcolor: index % 2 === 0 ? 'white' : '#f8f9fa' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.8rem' }}>
                        {student.nume?.[0]}{student.prenume?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {student.nume} {student.prenume}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {student.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={student.clasa || 'N/A'} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={student.specializare || 'N/A'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${student.totalCredits} credite`} 
                      size="small" 
                      color={student.totalCredits > 0 ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {student.an_scolar_curent || getCurrentSchoolYear()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Vezi istoric">
                      <IconButton size="small" onClick={() => handleOpenHistoryDialog(student)}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Schimbă clasa">
                      <IconButton size="small" color="primary" onClick={() => handleOpenChangeDialog(student)}>
                        <TransferIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAndSortedStudents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nu există elevi care să corespundă filtrelor selectate.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Tab 3: Promovare în Masă */}
      {tabValue === 2 && (
        <Box>
          {/* Header cu butoane de acțiune */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Promovare în Masă - An Școlar {getCurrentSchoolYear()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Selectează elevii pe care dorești să îi promovezi în anul următor
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<SelectAllIcon />}
                    onClick={selectAll}
                  >
                    Selectează Toți ({promotableStudents.length})
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    color="secondary"
                    startIcon={<DeselectIcon />}
                    onClick={deselectAll}
                    disabled={selectedStudents.size === 0}
                  >
                    Deselectează Toți
                  </Button>
                </Box>
              </Box>
              
              {selectedStudents.size > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Alert 
                    severity="info" 
                    action={
                      <Button 
                        color="primary" 
                        variant="contained" 
                        size="small"
                        onClick={() => setPromotionDialogOpen(true)}
                        startIcon={<TrendingUpIcon />}
                      >
                        Promovează {selectedStudents.size} elevi
                      </Button>
                    }
                  >
                    <strong>{selectedStudents.size}</strong> elevi selectați pentru promovare
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Lista de clase cu elevi */}
          <Grid container spacing={2}>
            {Object.entries(studentsForPromotionByClass)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([clasa, studentsInClass]) => {
                const isExpanded = expandedClasses.has(clasa);
                const selectedInClass = studentsInClass.filter(s => selectedStudents.has(s.id)).length;
                const allSelectedInClass = selectedInClass === studentsInClass.length;
                const someSelectedInClass = selectedInClass > 0 && !allSelectedInClass;
                const nextClass = studentsInClass[0]?.nextClass;

                return (
                  <Grid item xs={12} key={clasa}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        {/* Header clasă */}
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                          onClick={() => toggleClassExpand(clasa)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Checkbox
                              checked={allSelectedInClass}
                              indeterminate={someSelectedInClass}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleClassSelection(clasa, studentsInClass);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                Clasa {clasa}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {studentsInClass.length} elevi • Promovare la: {' '}
                                <Chip 
                                  label={nextClass === 'ABSOLVENT' ? 'Absolvent' : nextClass} 
                                  size="small" 
                                  color={nextClass === 'ABSOLVENT' ? 'success' : 'primary'}
                                  sx={{ height: 20 }}
                                />
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={`${selectedInClass}/${studentsInClass.length} selectați`}
                              size="small"
                              color={selectedInClass > 0 ? 'primary' : 'default'}
                              variant={selectedInClass > 0 ? 'filled' : 'outlined'}
                            />
                            <IconButton size="small">
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Lista elevilor (expandable) */}
                        <Collapse in={isExpanded}>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ pl: 2 }}>
                            {studentsInClass.map((student) => (
                              <Box 
                                key={student.id}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between',
                                  py: 0.5,
                                  px: 1,
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedStudents.has(student.id)}
                                      onChange={() => toggleStudentSelection(student.id)}
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                                        {student.nume?.[0]}{student.prenume?.[0]}
                                      </Avatar>
                                      <Typography variant="body2">
                                        {student.nume} {student.prenume}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip 
                                    label={`${student.totalCredits} credite`} 
                                    size="small" 
                                    variant="outlined"
                                    color={student.totalCredits > 0 ? 'success' : 'default'}
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    → {student.nextClass === 'ABSOLVENT' ? 'Abs.' : student.nextClass}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>

          {promotableStudents.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Nu există elevi eligibili pentru promovare.
            </Alert>
          )}
        </Box>
      )}

      {/* Dialog confirmare promovare în masă */}
      <Dialog 
        open={promotionDialogOpen} 
        onClose={() => !promotionInProgress && setPromotionDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Confirmare Promovare în Masă
        </DialogTitle>
        <DialogContent>
          {promotionInProgress ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Se promovează elevii...
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(promotionProgress.current / promotionProgress.total) * 100}
                sx={{ my: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {promotionProgress.current} / {promotionProgress.total} elevi procesați
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Atenție!</strong> Această acțiune va promova {selectedStudents.size} elevi în anul următor.
              </Alert>

              <Typography variant="body1" gutterBottom>
                <strong>Rezumat:</strong>
              </Typography>
              
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 2 }}>
                {Object.entries(studentsForPromotionByClass)
                  .filter(([_, studs]) => studs.some(s => selectedStudents.has(s.id)))
                  .map(([clasa, studs]) => {
                    const selectedCount = studs.filter(s => selectedStudents.has(s.id)).length;
                    const nextClass = studs[0]?.nextClass;
                    return (
                      <Box key={clasa} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          Clasa {clasa} → {nextClass === 'ABSOLVENT' ? 'Absolvenți' : nextClass}
                        </Typography>
                        <Chip label={`${selectedCount} elevi`} size="small" />
                      </Box>
                    );
                  })}
              </Box>

              <Alert severity="info">
                Creditele vor fi înregistrate în istoric și se reportează automat în noul an școlar.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPromotionDialogOpen(false)}
            disabled={promotionInProgress}
          >
            Anulează
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleMassPromotion}
            disabled={promotionInProgress}
            startIcon={<CheckCircleIcon />}
          >
            Confirmă Promovarea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog schimbare clasă */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Schimbare Clasă - {selectedStudent?.nume} {selectedStudent?.prenume}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Info actuală */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Situație actuală:</strong> Clasa {selectedStudent?.clasa || 'N/A'}, 
              Specializare {selectedStudent?.specializare || 'N/A'}, 
              {selectedStudent?.totalCredits || 0} credite acumulate
            </Alert>

            <Grid container spacing={2}>
              {/* Mai întâi alegem specializarea, apoi clasa */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Specializare"
                  value={changeData.specializare_noua}
                  onChange={(e) => setChangeData({ 
                    ...changeData, 
                    specializare_noua: e.target.value,
                    clasa_noua: '' // Reset clasa când schimbăm specializarea
                  })}
                >
                  {specializations.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Clasa Nouă"
                  value={changeData.clasa_noua}
                  onChange={(e) => setChangeData({ ...changeData, clasa_noua: e.target.value })}
                  disabled={!changeData.specializare_noua}
                  helperText={!changeData.specializare_noua ? 'Selectează mai întâi specializarea' : ''}
                >
                  {getClassesForSpecialization(changeData.specializare_noua).map((c) => (
                    <MenuItem key={c} value={c}>
                      Clasa {c} ({getSpecializationName(changeData.specializare_noua)})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Motiv Schimbare"
                  value={changeData.motiv}
                  onChange={(e) => setChangeData({ ...changeData, motiv: e.target.value })}
                >
                  {motiveSchimbare.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {selectedStudent?.totalCredits > 0 && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                <strong>Credite:</strong> Elevul are {selectedStudent.totalCredits} credite care vor fi înregistrate în istoric
                și marcate ca transferabile pentru noul an școlar.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Anulează</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveClassChange}
            disabled={saving || !changeData.clasa_noua}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Se salvează...' : 'Salvează Schimbarea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog istoric */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Istoric - {selectedStudent?.nume} {selectedStudent?.prenume}
        </DialogTitle>
        <DialogContent>
          {classHistory.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Data</strong></TableCell>
                    <TableCell><strong>De la</strong></TableCell>
                    <TableCell align="center"><ArrowForwardIcon fontSize="small" /></TableCell>
                    <TableCell><strong>La</strong></TableCell>
                    <TableCell><strong>Motiv</strong></TableCell>
                    <TableCell><strong>Credite</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classHistory.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {new Date(entry.data_schimbare).toLocaleDateString('ro-RO')}
                      </TableCell>
                      <TableCell>
                        {entry.clasa_veche || 'N/A'} ({entry.specializare_veche || 'N/A'})
                      </TableCell>
                      <TableCell align="center">→</TableCell>
                      <TableCell>
                        {entry.clasa_noua} ({entry.specializare_noua || 'N/A'})
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getMotivLabel(entry.motiv)} 
                          size="small" 
                          color={getMotivColor(entry.motiv)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="caption" display="block">
                            Total: {entry.credite_totale_la_schimbare}
                          </Typography>
                          <Typography variant="caption" color="success.main">
                            Transferate: {entry.credite_transferate}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              Nu există istoric de schimbări pentru acest elev.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
